import React from 'react';
import { Neo, NeoGrid } from '@singularsystems/neo-react';
import { observer } from 'mobx-react';;
import { EnumHelper } from '@singularsystems/neo-core';
import { InterestRateModifier } from '../../../Models/Rates/InterestRateModifier.enum';
import InterestRate from '../../../Models/Rates/InterestRate';
import InterestRateTypesVM from '../InterestRateTypesVM';
import InterestRateType from '../../../Models/Rates/InterestRateType';
import AddChildInterestRateTypeCommand from '../../../Models/Rates/Commands/AddChildInterestRateTypeCommand';
import { InterestCalculationType } from '../../../Models/Rates/InterestCalculationType.enum';
import DateCycle from '../../../Models/Dividends/DateCycle';
import { DateCycleEditComponent } from './DateCycleEdit';
import DateCycleDropDown from '../../Investments/Components/DateCycleDropDown';
import { DateCycleType } from '../../../Models/Dividends/DateCycleType';
import InterestRateImportResult from '../../../Models/Import/InterestRateImportResult';

export interface IInterestRateTypeListComponentProps {
    viewModel: InterestRateTypesVM;
    interestRate?: InterestRate;
}

@observer
export class InterestRateTypeComponent extends React.Component<IInterestRateTypeListComponentProps> {

    constructor(props: IInterestRateTypeListComponentProps) {
        super(props);
    }

    public render() {
        const viewModel = this.props.viewModel;

        if (viewModel.editingRateType) {
            return this.renderInterestRateList(viewModel.editingRateType, viewModel);
        } else {
            return this.renderInterestRateTypeList(viewModel);
        }
    }

    public renderInterestRateTypeList(viewModel: InterestRateTypesVM) {
        return (
            <Neo.Form model={viewModel.interestRateTypeList} showSummaryModal onSubmit={() => viewModel.saveInterestRateTypes()}>
                <div style={{ textAlign: "right" }} className="mb-2">
                    <Neo.Button isSubmit icon="save" disabled={!viewModel.interestRateTypeList.isDirty} pulse={viewModel.interestRateTypeList.isDirty}>Save</Neo.Button>
                </div>
                <NeoGrid.Grid items={viewModel.interestRateTypeList} showAddButton>
                    {(item, meta) => (
                        <NeoGrid.RowGroup expandProperty={meta.isExpanded} >
                            <NeoGrid.Row>
                                <NeoGrid.Column bind={meta.rateTypeName} />
                                <NeoGrid.Column bind={meta.shortName} width={120} />
                                <NeoGrid.Column bind={meta.description} />
                                <NeoGrid.Column bind={meta.productTypeId} select={{ itemSource: viewModel.dataCache.productTypes, displayMember: "productTypeCode", valueMember: "productTypeId" }} />
                                <NeoGrid.Column bind={meta.exemptOnly} label="Exempt only?" width={120} />
                                <NeoGrid.Column bind={meta.issuerOnly} label="Issuers only?" width={120} />
                                <NeoGrid.Column bind={meta.isPrime}
                                    label="Is Prime?"
                                    width={120}
                                    cellTooltip="Indicates that the interest rate type will be used in reports that use the Prime rate"
                                    disabled={item.isZaronia} />
                                <NeoGrid.Column bind={meta.isActive} label="Is Active?" width={90} />
                                <NeoGrid.Column bind={meta.ordinal} width={60} />
                                <NeoGrid.Column
                                    bind={meta.interestCalculationType}
                                    disabled={item.isLinkedToInvestments}
                                    label="Calculation Type"
                                    select={{ items: EnumHelper.asList(InterestCalculationType) }}
                                    onChange={() => {
                                        if (item.isZaronia) {
                                            item.modifierType = InterestRateModifier.Add;
                                            item.isPrime = false;
                                        }
                                    }} />
                                <NeoGrid.Column
                                    bind={meta.modifierType}
                                    disabled={item.isLinkedToInvestments || item.isZaronia}
                                    select={{ items: EnumHelper.asList(InterestRateModifier) }} />
                                <NeoGrid.ButtonColumn
                                    editButton={{ icon: "list", onClick: () => viewModel.beginEditingInterestRate(item), disabled: item.isNew }}
                                    deleteButton={{ disabled: item.isLinkedToInvestments }} />
                            </NeoGrid.Row>
                            <NeoGrid.ChildRow>
                                <NeoGrid.Grid items={item.childInterestRateTypes} >
                                    {(childInterestRateType, meta) => (
                                        <NeoGrid.Row>
                                            <NeoGrid.Column bind={meta.rateTypeName} />
                                            <NeoGrid.Column bind={meta.shortName} width={120} />
                                            <NeoGrid.Column bind={meta.description} />
                                            <NeoGrid.Column bind={meta.productTypeId} select={{ itemSource: viewModel.dataCache.productTypes, displayMember: "productTypeCode", valueMember: "productTypeId" }} />
                                            <NeoGrid.Column bind={meta.exemptOnly} label="Exempt only?" width={120} />
                                            <NeoGrid.Column bind={meta.issuerOnly} label="Issuers only?" width={120} />
                                            <NeoGrid.Column bind={meta.dayOffset} label="Business Days Offset" width={90} disabled={true} />
                                            <NeoGrid.Column bind={meta.isActive} label="Is Active?" width={90} />
                                            <NeoGrid.Column bind={meta.ordinal} width={60} />
                                            <NeoGrid.Column
                                                bind={meta.interestCalculationType}
                                                disabled={childInterestRateType.isLinkedToInvestments}
                                                label="Calculation Type"
                                                select={{ items: EnumHelper.asList(InterestCalculationType) }}
                                                onChange={() => {
                                                    if (childInterestRateType.isZaronia) {
                                                        childInterestRateType.modifierType = InterestRateModifier.Add;
                                                    }
                                                }} />
                                            <NeoGrid.Column
                                                bind={meta.modifierType}
                                                disabled={childInterestRateType.isLinkedToInvestments || childInterestRateType.isZaronia}
                                                select={{ items: EnumHelper.asList(InterestRateModifier) }} />
                                            <NeoGrid.ButtonColumn
                                                editButton={{ icon: "list", onClick: () => viewModel.beginEditingInterestRate(childInterestRateType) }}
                                                deleteButton={{ disabled: childInterestRateType.isLinkedToInvestments }} />
                                        </NeoGrid.Row>
                                    )}
                                </NeoGrid.Grid>
                            </NeoGrid.ChildRow>
                        </NeoGrid.RowGroup>
                    )}
                </NeoGrid.Grid>
            </Neo.Form>
        )
    }

    public renderInterestRateList(interestRateType: InterestRateType, viewModel: InterestRateTypesVM) {
        return (
            <Neo.Form model={viewModel.pageManager.data} showSummaryModal onSubmit={() => viewModel.saveInterestRates()} >
                <div className="d-flex justify-content-between mb-4">
                    <Neo.Button icon="arrow-left" variant="light" className="min-width" onClick={() => { viewModel.editingRateType = null; viewModel.pageManager.data.set([]); }}>Back</Neo.Button>
                    <Neo.Button isSubmit icon="save" disabled={(!viewModel.pageManager.data.readyToSave && !interestRateType.readyToSave) || interestRateType.isChild} pulse={!interestRateType.isChild && (viewModel.pageManager.data.readyToSave || interestRateType.readyToSave)}>Save</Neo.Button>
                </div>

                {!interestRateType.isChild && viewModel.importValidation === null &&
                    <Neo.Card title="Import" icon="folder">
                        <Neo.GridLayout sm={3}>
                            <Neo.FormGroup bind={interestRateType.meta.importConfigId} label="Config" select={{ itemSource: viewModel.dataCache.importConfig }} />
                            <Neo.FormGroup bind={interestRateType.meta.importRowIdentifier} label="Row Identifier" footerText="The filter row value that identifies interest rates for this interest rate type." />
                            {interestRateType.importConfigId &&
                                <Neo.FormGroup bindContext={interestRateType.meta.interestRateTypeId} label="Upload File" disabled={interestRateType.readyToSave || viewModel.pageManager.data.readyToSave} >
                                    <Neo.FileContext fileManager={viewModel.fileManager}>
                                        <Neo.FileDropArea >
                                            <Neo.FileUploadButton variant="primary" disabled={viewModel.importValidation !== null || interestRateType.readyToSave || viewModel.pageManager.data.readyToSave}>Select file</Neo.FileUploadButton> or drag a file here.
                                            <Neo.FileUploadProgress />
                                        </Neo.FileDropArea>
                                    </Neo.FileContext>
                                </Neo.FormGroup>}
                        </Neo.GridLayout>
                    </Neo.Card >}

                {!interestRateType.isChild &&
                    <Neo.Card title="Details">
                        <Neo.GridLayout sm={2}>
                            <DateCycleDropDown viewModel={viewModel} id={interestRateType.meta.rateFrequencyId} display={interestRateType.meta.rateFrequencyName}
                                navigation={interestRateType.meta.rateFrequency} filterType={DateCycleType.RateFrequency} formClassName="mb-0" label="Rate Frequency" />
                            <Neo.Button
                                icon="clipboard"
                                disabled={interestRateType.readyToSave || viewModel.pageManager.data.readyToSave}
                                onClick={() => viewModel.addDayOffsetCommand = new AddChildInterestRateTypeCommand(interestRateType.interestRateTypeId, interestRateType.isLinkedToInvestments, interestRateType.rateFrequencyName)} >
                                Add Offset Rate Type
                            </Neo.Button>
                        </Neo.GridLayout>
                    </Neo.Card>}
                <Neo.Card title={"Editing rates for " + interestRateType.rateTypeName} >
                    <Neo.Pager pageManager={viewModel.pageManager} pageControls="bottom">
                        <Neo.GridLayout lg={1} xl={2}>
                            <NeoGrid.Grid<InterestRate>
                                addButton={interestRateType.isChild ? undefined : { onClick: () => viewModel.newInterestRate() }} >
                                {(item, meta) => (
                                    <NeoGrid.Row>
                                        <NeoGrid.Column bind={meta.effectiveDate} disabled={interestRateType.isChild} dateProps={{ formatString: "dd MMM yyyy (ddd)" }} />
                                        <NeoGrid.Column bind={meta.rate} appendText="%" disabled={interestRateType.isChild} />
                                        {interestRateType.isChild && <NeoGrid.Column bind={meta.lookbackDate} dateProps={{ formatString: "dd MMM yyyy (ddd)" }} disabled />}
                                        {!interestRateType.isChild && <NeoGrid.ButtonColumn showDelete />}
                                    </NeoGrid.Row>
                                )}
                            </NeoGrid.Grid>
                        </Neo.GridLayout>
                    </Neo.Pager>
                </Neo.Card>
                <Neo.Modal
                    title="Edit rate frequency cycle"
                    bindModel={viewModel.dateCycleVM.meta.editingDateCycle}
                    size="lg"
                    acceptButton={{ onClick: () => viewModel.saveDateCycle() }}>
                    {(dateCycle: DateCycle) =>
                        <Neo.FormContext allReadOnly={viewModel.dateCycleVM.isReadOnly}>
                            <DateCycleEditComponent viewModel={viewModel.dateCycleVM} dateCycle={dateCycle} inModal />
                        </Neo.FormContext>
                    }
                </Neo.Modal>
                <Neo.Modal
                    title="Add Day Offset"
                    size="sm"
                    bindModel={viewModel.meta.addDayOffsetCommand}
                    acceptButton={{
                        text: "Add Day Offset",
                        icon: "save",
                        onClick: () => {
                            viewModel.addInterestRateTypeChild(interestRateType);
                        }
                    }}>
                    {(command: AddChildInterestRateTypeCommand) => (
                        <div>
                            <Neo.FormGroupInline bind={command.meta.dayOffset} />
                        </div>
                    )}
                </Neo.Modal>
                <Neo.Modal
                    title="File Validation"
                    bindModel={viewModel.meta.importValidation}
                    size="lg"
                    acceptButton={{ onClick: () => viewModel.importValidation = null }}>
                    {(importResult: InterestRateImportResult) =>
                        <>
                            <Neo.Alert variant="danger" heading="Please note the following errors:">
                                The uploaded file contains the following errors:
                                <div className="mt-3">
                                    {importResult.fileErrors.map((err, index) => (
                                        <div key={index}>{err}</div>
                                    ))}
                                    {importResult.rowErrors.map((err, index) => (
                                        <div key={index}>{err}</div>
                                    ))}
                                </div>
                            </Neo.Alert>
                        </>
                    }
                </Neo.Modal>
            </Neo.Form >
        )
    }
}